/* eslint-disable max-lines -- Pre-existing violation */
import {startOfToday} from 'date-fns';
import {
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  DialogFooter,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  isCurrency,
  Segment,
  Separator,
  Tooltip,
} from 'platform/components';
import {Box, Grid, GridItem, HStack, Icon, Show, Space, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {z} from 'zod';

import {Fragment, RefObject, useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {defaultTo, isNil, isNotNil, not} from 'ramda';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetEmployeeMechanicsQuery} from '@dms/api/customer';
import {useGetOperationItemsQuery} from '@dms/api/finance';
import {
  DiscountSourceType,
  GetDirectSaleMaterialItemResponse,
  useGetDirectSaleQuery,
  useGetDirectSaleVariantQuery,
  useGetMaterialPriceTypesQuery,
  useGetSupplierDiscountGroupsQuery,
  useGetWarehousesQuery,
  usePatchDirectSaleMaterialItemMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  useTenant,
  getDecimalFromPercentage,
  getInvoiceName,
  getOptionsFromWarehouses,
  getPercentageFromDecimal,
  getSourceNameTooltip,
  handleApiError,
  useGetVatRatesOptions,
  getOptionsFromManufacturers,
} from '@dms/shared';
import {
  getEditItemQuantitySchema,
  getOptionsFromMaterialPriceTypes,
  getOptionsFromMechanics,
} from '@dms/shared-aftersales';
import {DEFAULT_VAT_RATE, getOptionsFromSupplierDiscountGroups} from '@dms/warehouse/shared';

import {
  composePath,
  generateHashFromObjects,
  getApiDateString,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {
  EditMaterialItemForm,
  EditMaterialItemFormSchema,
} from '../../../../types/EditMaterialItemForm';
import {RequestTabMaterialAvailabilityTab} from './RequestTabMaterialAvailabilityTab';
import {
  MaterialItemCurrentPrices,
  RequestTabMaterialItemPrice,
} from './RequestTabMaterialItemPrice';
import {RequestTabMaterialRequestsTab} from './RequestTabMaterialRequestsTab';

enum ListSegment {
  availability = 'availability',
  requests = 'requests',
}

export interface RequestTabMaterialItemEditProps extends RequiredTestIdProps {
  directSaleId: string;
  item: GetDirectSaleMaterialItemResponse;
  authorizationProfileId: string | Nullish;
  onClose: VoidFunction;
  isEditingDisabled?: boolean;
  isSavedByRowControlRef?: RefObject<boolean>;
  formRef?: RefObject<{
    submit: () => Promise<boolean>;
    isDirty: boolean;
  } | null>;
}

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export function RequestTabMaterialItemEdit(props: RequestTabMaterialItemEditProps) {
  const materialItem = props.item;
  const formatCurrency = useFormatCurrency();
  const todayDate = startOfToday();
  const {tenantCurrency} = useTenant();
  const [listSegment, setListSegment] = useState<ListSegment>(ListSegment.availability);

  const directSaleQuery = useGetDirectSaleQuery({directSaleId: props.directSaleId});

  const directSaleVariantQuery = useGetDirectSaleVariantQuery(
    {directSaleVariantId: directSaleQuery.data?.directSaleVariantId as string},
    {skip: isNil(directSaleQuery.data?.directSaleVariantId)}
  );

  const materialPriceTypesQuery = useGetMaterialPriceTypesQuery();

  const {data: warehouses, isLoading: isWarehousesLoading} = useGetWarehousesQuery();

  const {data: manufacturers, isLoading: isManufacturersLoading} = useGetManufacturersQuery();

  const {data: mechanics, isLoading: isMechanicsLoading} = useGetEmployeeMechanicsQuery(
    {
      authorizationProfileId: defaultTo('', props.authorizationProfileId),
      isActive: true,
      withToOffer: true,
    },
    {
      skip: isNil(props.authorizationProfileId),
    }
  );

  const supplierDiscountGroupsQuery = useGetSupplierDiscountGroupsQuery(
    {
      supplierId: materialItem.defaultSupplierId as string,
      authorizationProfileId: props.authorizationProfileId as string,
      discountGroupId: materialItem.defaultSupplierDiscountGroup,
    },
    {skip: isNil(materialItem.defaultSupplierId) || isNil(props.authorizationProfileId)}
  );

  const {options: workItemGroupOptions, isLoading: isWorkItemGroupLoading} =
    useGetOperationItemsQuery(
      {application: 'workshop', itemGroup: 'material'},
      {
        selectFromResult: (data) => ({
          ...data,
          options:
            data?.data?.map((item) => ({label: item.itemCodeName, value: item.operationId})) ?? [],
        }),
      }
    );

  const [patchMaterialItem, patchMaterialItemStatus] = usePatchDirectSaleMaterialItemMutation({
    fixedCacheKey: 'edit-direct-sale-material-item',
  });

  const [vatOptions] = useGetVatRatesOptions({fullLabel: true});

  const isLoading =
    directSaleQuery.isLoading ||
    directSaleVariantQuery.isLoading ||
    materialPriceTypesQuery.isLoading ||
    isWarehousesLoading ||
    isManufacturersLoading ||
    isWorkItemGroupLoading;

  const isEditingDisabled = props.isEditingDisabled || not(materialItem.isAllowedItemEditing);

  const handleSubmit: FormSubmitHandler<EditMaterialItemForm> = async (formValues) => {
    await patchMaterialItem({
      directSaleId: props.directSaleId,
      itemId: props.item.id,
      body: {
        name: formValues.name ?? '',
        quantity: formValues.quantity,
        isUnitPriceWithVat: formValues.isUnitPriceWithVat,
        sellingPricePerUnit: formValues.sellingPricePerUnit,
        vatType: formValues.vatType,
        vatFreeCode: formValues.vatFreeCode,
        mechanic: formValues.mechanic,
        isCustomPrice: formValues.isCustomPrice,
        neededAt: formValues.neededAt ? getApiDateString(formValues.neededAt) : null,
        workItemGroupId: formValues.workItemGroupId,
        discount: {
          isDiscountEnable: formValues.discount.isDiscountEnable,
          discountSourceType: formValues.discount.isDiscountEnable
            ? formValues.discount.discountSourceType
            : null,
          discountRate: formValues.discount.isDiscountEnable
            ? (getDecimalFromPercentage(formValues.discount.discountRate) ?? 0)
            : 0,
          isDoNotApplyDiscount: formValues.discount.isDoNotApplyDiscount,
        },
        isOneTimeSale: formValues.isOneTimeSale,
      },
    })
      .unwrap()
      .then(() => {
        // Saving changes by rowControl change should not close dialog.
        if (props.isSavedByRowControlRef?.current) {
          props.isSavedByRowControlRef.current = false;
          return;
        }
        props.onClose();
      })
      .catch(handleApiError);
  };

  const handleArticleDetail = () => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {
          warehouseId: materialItem?.warehouse?.warehouseId,
          id: materialItem?.warehouse.warehouseArticleId,
        },
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const handlePriceReset = (formApi: UseFormReturn<EditMaterialItemForm>) => {
    const isCustomPrice = formApi.watch('isCustomPrice');

    if (isCustomPrice) {
      return;
    }

    formApi.setValue(
      'sellingPricePerUnit',
      defaultTo(0, materialItem?.baseSellingPricePerUnit?.amount)
    );
  };

  const handleDiscountChange = (
    index: number,
    formApi: UseFormReturn<EditMaterialItemForm>,
    discountSourceType: DiscountSourceType,
    discountRate: number | Nullish
  ) => {
    formApi.clearErrors(`discount.availableDiscounts.${index}.discountRate`);

    const validationResult = discountRateSchema.safeParse(discountRate);

    if (validationResult.success) {
      formApi.setValue('discount.discountSourceType', discountSourceType);
      formApi.setValue('discount.discountRate', discountRate);
      return;
    }

    const errorMessages = validationResult.error?.errors.map((error) => error.message).join(' ');
    formApi.setError(`discount.availableDiscounts.${index}.discountRate`, {message: errorMessages});
  };

  const handleDoNotApplyDiscount = (formApi: UseFormReturn<EditMaterialItemForm>) => {
    const shouldNotApplyDiscount = formApi.watch('discount.isDoNotApplyDiscount');

    if (shouldNotApplyDiscount) {
      formApi.setValue('discount.isDiscountEnable', false);
      formApi.setValue('discount.discountRate', 0);

      // Get the current list of available discounts
      const availableDiscounts = formApi.getValues('discount.availableDiscounts') || [];

      availableDiscounts.forEach((_, index) => {
        formApi.setValue(`discount.availableDiscounts.${index}.discountRate`, 0);
      });
    }
  };

  const handlePriceChange = (
    currentPrices: MaterialItemCurrentPrices,
    formApi: UseFormReturn<EditMaterialItemForm>
  ) => {
    formApi.setValue('sellingPricePerUnit', currentPrices.sellingPricePerUnit, {shouldDirty: true});
    formApi.setValue('isCustomPrice', currentPrices.isCustomPrice, {shouldDirty: true});
    formApi.setValue('discount.isDiscountEnable', currentPrices.isDiscountEnabled, {
      shouldDirty: true,
    });
    formApi.setValue('discount.isDoNotApplyDiscount', currentPrices.isDoNotApplyDiscount, {
      shouldDirty: true,
    });
  };

  const validateQuantity = (value: number | null, formApi: UseFormReturn<EditMaterialItemForm>) => {
    const valueToValidate = defaultTo(0, value);

    formApi.clearErrors('quantity');

    const validationResult = quantitySchema.safeParse(valueToValidate);

    if (validationResult.success) {
      return;
    }

    const errorMessages = validationResult.error?.errors.map((error) => error.message).join(' ');
    formApi.setError('quantity', {message: errorMessages});
  };

  const dispensingUnit = defaultTo(1, materialItem?.dispensingUnit);

  const quantitySchema = getEditItemQuantitySchema({
    dispensingUnit,
    currentQuantity: materialItem.quantity.amount,
    minQuantity: materialItem.minMaxQuantity?.minQuantity,
    maxQuantity: materialItem.minMaxQuantity?.maxQuantity,
  });

  const formSchema = EditMaterialItemFormSchema.extend({quantity: quantitySchema});

  const currency = materialItem?.sellingPricePerUnit?.currency;

  const baseVat = vatOptions.find((vat) => vat.value === materialItem?.baseSellingPriceVat?.type);

  const basePrice = `${i18n.t('general.labels.default')}: ${formatCurrency(
    defaultTo(0, materialItem?.baseSellingPricePerUnit?.amount),
    defaultTo(tenantCurrency, materialItem?.baseSellingPricePerUnit?.currency)
  )}, ${i18n.t('general.labels.vat')}: ${baseVat?.rate} %`;

  const activeDiscount = materialItem?.itemsDiscounts.availableDiscounts?.find(
    (discount) => discount.isSet
  );

  const availableDiscounts =
    materialItem?.itemsDiscounts.availableDiscounts?.map((availableDiscount) => ({
      ...availableDiscount,
      discountRate: getPercentageFromDecimal(availableDiscount.discountRate) ?? 0,
    })) ?? [];

  const defaultValues: EditMaterialItemForm = {
    serialNumber: materialItem?.serialNumber,
    catalogNumber: materialItem?.number,
    manufacturerId: materialItem?.manufacturerId,
    name: getInvoiceName(materialItem),
    warehouseId: materialItem?.warehouse?.warehouseId,
    storageLocation: materialItem?.warehouse?.storageLocation,
    rabatGroupId: materialItem?.defaultSupplierDiscountGroup,
    priceType: directSaleVariantQuery.data?.general?.priceType,
    neededAt: parseDate(materialItem?.neededAt) ?? new Date(),
    workItemGroupId: materialItem?.workItemGroupId,
    isOneTimeSale: materialItem.isOneTimeSale ?? false,
    quantity: defaultTo(0, materialItem?.quantity.amount),
    sellingPricePerUnit: defaultTo(0, materialItem?.sellingPricePerUnit?.amount),
    mechanic: materialItem?.assignMechanic,
    vatType: defaultTo(DEFAULT_VAT_RATE, materialItem?.sellingPriceVat?.type),
    vatFreeCode: materialItem?.sellingPriceVat?.freeCode,
    isUnitPriceWithVat: defaultTo(false, materialItem?.isUnitPriceWithVat),
    isCustomPrice: defaultTo(false, materialItem?.isCustomPrice),
    discount: {
      isDoNotApplyDiscount: defaultTo(false, materialItem?.itemsDiscounts.isDoNotApplyDiscount),
      isDiscountEnable: defaultTo(false, materialItem?.itemsDiscounts.isDiscountEnable),
      discountSourceType: activeDiscount?.discountSourceType,
      discountRate: getPercentageFromDecimal(activeDiscount?.discountRate) ?? 0,
      availableDiscounts,
    },
  };

  const segments = [
    {
      value: ListSegment.availability,
      label: i18n.t('entity.warehouse.labels.availability'),
      content: (
        <RequestTabMaterialAvailabilityTab
          manufacturerNumber={materialItem?.manufacturerNumber}
          manufacturerId={materialItem?.manufacturerId}
          data-testid={suffixTestId('availabilityTab', props)}
        />
      ),
    },
    {
      value: ListSegment.requests,
      label: i18n.t('entity.warehouse.labels.requests'),
      content: (
        <RequestTabMaterialRequestsTab
          directSaleItemId={materialItem?.id}
          data-testid={suffixTestId('requestsTab', props)}
        />
      ),
    },
  ];

  const segment = segments.find((item) => item.value === listSegment)?.content;

  return (
    <DataStatus isLoading={isLoading} minHeight={140} key={props.item.id}>
      <Form<EditMaterialItemForm>
        key={generateHashFromObjects(materialItem)}
        defaultValues={defaultValues}
        experimentalZodSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {/* eslint-disable-next-line max-lines-per-function -- Pre-existing violation */}
        {(control, formApi) => {
          // Used for saving changes by rowControl in parent.
          const formRef = props.formRef;
          if (formRef) {
            formRef.current = {
              submit: () => control.submitForm(),
              isDirty: formApi.formState.isDirty,
            };
          }

          const isTouched = formApi.formState.isDirty;
          const hasQuantityError = isNotNil(formApi.formState.errors.quantity);
          const isCustomPrice = formApi.watch('isCustomPrice');
          const discount = formApi.watch('discount');

          return (
            <VStack spacing={4}>
              <Card variant="inlineGrey">
                <VStack spacing={4}>
                  <Grid spacing={4} columns={8}>
                    <GridItem span={4}>
                      <HStack spacing={4}>
                        <Box width={13}>
                          <FormField
                            isDisabled
                            control={control}
                            type="number"
                            name="serialNumber"
                            label={i18n.t('entity.warehouse.labels.sNo')}
                          />
                        </Box>
                        <Box width={50}>
                          <FormField
                            isDisabled
                            control={control}
                            type="text"
                            name="catalogNumber"
                            label={i18n.t('entity.warehouse.labels.catalogNumber')}
                          />
                        </Box>
                        <Box width={68}>
                          <FormField
                            isDisabled
                            control={control}
                            type="choice"
                            name="manufacturerId"
                            label={i18n.t('entity.warehouse.labels.manufacturer')}
                            options={getOptionsFromManufacturers(manufacturers)}
                          />
                        </Box>
                      </HStack>
                    </GridItem>
                    <GridItem span={4}>
                      <FormField
                        isRequired
                        isDisabled={isEditingDisabled}
                        control={control}
                        type="text"
                        name="name"
                        label={i18n.t('entity.warehouse.labels.name')}
                        tooltip={getSourceNameTooltip(materialItem?.name)}
                        data-testid={suffixTestId('inputs.name', props)}
                      />
                    </GridItem>
                  </Grid>

                  <Grid spacing={4} columns={4}>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="choice"
                        name="warehouseId"
                        label={i18n.t('entity.warehouse.labels.warehouse')}
                        options={getOptionsFromWarehouses(warehouses)}
                        data-testid={suffixTestId('inputs.warehouse', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="text"
                        name="storageLocation"
                        label={i18n.t('entity.warehouse.labels.storageLocation')}
                        data-testid={suffixTestId('inputs.storageLocation', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        isLoading={supplierDiscountGroupsQuery.isLoading}
                        control={control}
                        type="choice"
                        name="rabatGroupId"
                        label={i18n.t('entity.warehouse.labels.rabatGroup')}
                        options={getOptionsFromSupplierDiscountGroups(
                          supplierDiscountGroupsQuery.data
                        )}
                        data-testid={suffixTestId('inputs.discountGroup', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled
                        control={control}
                        type="choice"
                        name="priceType"
                        label={i18n.t('entity.warehouse.labels.priceType')}
                        options={getOptionsFromMaterialPriceTypes(materialPriceTypesQuery.data)}
                      />
                    </GridItem>
                  </Grid>

                  <Grid spacing={4} columns={4} align="flex-end">
                    <GridItem span={1}>
                      <FormField
                        name="neededAt"
                        type="date"
                        minDate={todayDate}
                        control={control}
                        label={i18n.t('entity.warehouse.labels.neededAt')}
                        isDisabled={isEditingDisabled}
                        data-testid={suffixTestId('inputs.neededAt', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        isDisabled={isEditingDisabled}
                        isLoading={isWorkItemGroupLoading}
                        control={control}
                        type="choice"
                        name="workItemGroupId"
                        label={i18n.t('entity.warehouse.labels.workItemGroup')}
                        options={workItemGroupOptions}
                        menuInPortal
                        data-testid={suffixTestId('inputs.workItemGroup', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <HStack spacing={1} align="center">
                        <VStack justify="center">
                          <FormField
                            name="isOneTimeSale"
                            type="checkbox"
                            control={control}
                            label={i18n.t('entity.warehouse.labels.oneTimeSale')}
                            data-testid={suffixTestId('inputs.oneTimeSale', props)}
                          />
                          <Space vertical={2} />
                        </VStack>
                        <VStack justify="center">
                          <Tooltip
                            placement="top"
                            description={i18n.t('entity.warehouse.labels.oneTimeSaleTooltip')}
                          >
                            <Icon
                              value="action/help"
                              color="text.secondary"
                              cursor="pointer"
                              size={4}
                            />
                            <Space vertical={2} />
                          </Tooltip>
                        </VStack>
                      </HStack>
                    </GridItem>
                  </Grid>

                  <Separator spacing={0} />

                  <Grid spacing={4} columns={4}>
                    <GridItem span={1}>
                      <FormField
                        name="quantity"
                        type="number"
                        control={control}
                        label={i18n.t('entity.warehouse.labels.quantity')}
                        step={dispensingUnit}
                        isStepperVisible
                        shouldRoundStepsByDifference
                        onChange={(value) => validateQuantity(value, formApi)}
                        decimalPlaces={4}
                        isDisabled={
                          not(materialItem?.isAllowedQuantityEditing) || isEditingDisabled
                        }
                        data-testid={suffixTestId('inputs.quantity', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        name="sellingPricePerUnit"
                        type="currency"
                        control={control}
                        label={i18n.t('entity.warehouse.labels.pricePerUnit')}
                        currency={isCurrency(currency) ? currency : undefined}
                        isDisabled={not(isCustomPrice) || isEditingDisabled}
                        data-testid={suffixTestId('inputs.sellingPricePerUnit', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        name="vatType"
                        type="choice"
                        control={control}
                        label={i18n.t('entity.warehouse.labels.vat')}
                        options={vatOptions}
                        isDisabled={not(isCustomPrice) || isEditingDisabled}
                        isNotClearable
                        data-testid={suffixTestId('inputs.vat', props)}
                      />
                    </GridItem>
                    <GridItem span={1}>
                      <FormField
                        name="mechanic"
                        type="choice"
                        control={control}
                        label={i18n.t('entity.warehouse.labels.mechanic')}
                        options={getOptionsFromMechanics(mechanics)}
                        isLoading={isMechanicsLoading}
                        isDisabled={isEditingDisabled}
                        menuInPortal
                        data-testid={suffixTestId('inputs.mechanic', props)}
                      />
                    </GridItem>
                  </Grid>

                  <Show when={isCustomPrice}>
                    <HStack spacing={2}>
                      <Icon size={4} value="action/info" color="palettes.neutral.300.100" />
                      <Text
                        size="xSmall"
                        color="tertiary"
                        data-testid={suffixTestId('isCustomPriceHelper', props)}
                      >
                        {`${i18n.t(
                          'entity.warehouse.labels.customPriceDescription'
                        )} ${basePrice}.`}
                      </Text>
                    </HStack>
                  </Show>

                  <HStack>
                    <FormField
                      name="isCustomPrice"
                      type="switch"
                      control={control}
                      label={i18n.t('entity.warehouse.labels.customPrice')}
                      onChange={() => handlePriceReset(formApi)}
                      isDisabled={isEditingDisabled}
                      data-testid={suffixTestId('inputs.isCustomPrice', props)}
                    />
                  </HStack>

                  <HStack justify="space-between">
                    <FormField
                      name="discount.isDiscountEnable"
                      type="switch"
                      control={control}
                      label={i18n.t('general.labels.discount')}
                      isDisabled={isEditingDisabled || discount.isDoNotApplyDiscount}
                      data-testid={suffixTestId('inputs.isCustomDiscount', props)}
                    />
                    <FormField
                      control={control}
                      name="discount.isDoNotApplyDiscount"
                      label={i18n.t('entity.warehouse.labels.dontApplyDiscount')}
                      onChange={() => handleDoNotApplyDiscount(formApi)}
                      type="checkbox"
                      isDisabled={isEditingDisabled}
                      data-testid={suffixTestId('inputs.isDoNotApplyDiscount', props)}
                    />
                  </HStack>

                  <Show when={discount.isDiscountEnable}>
                    <Grid spacing={4} columns={4} align="center">
                      {discount.availableDiscounts?.map((discount, index) => (
                        <Fragment key={discount.discountSourceType}>
                          <GridItem span={1}>
                            <FormField
                              name="discount.discountSourceType"
                              type="radio"
                              control={control}
                              options={[
                                {
                                  label: discount.discountSourceTypeName,
                                  value: discount.discountSourceType,
                                  isDisabled: isEditingDisabled,
                                },
                              ]}
                              onChange={() =>
                                handleDiscountChange(
                                  index,
                                  formApi,
                                  discount.discountSourceType,
                                  discount.discountRate
                                )
                              }
                              data-testid={suffixTestId('discountRate', props)}
                            />
                          </GridItem>

                          <GridItem span={1}>
                            <FormField
                              name={`discount.availableDiscounts.${index}.discountRate`}
                              type="number"
                              control={control}
                              suffix="%"
                              minStepperValue={0}
                              maxStepperValue={100}
                              decimalPlaces={2}
                              isDisabled={not(discount.isChangeable) || isEditingDisabled}
                              onChange={() =>
                                handleDiscountChange(
                                  index,
                                  formApi,
                                  discount.discountSourceType,
                                  discount.discountRate
                                )
                              }
                              data-testid={suffixTestId('discountRate', props)}
                            />
                          </GridItem>
                        </Fragment>
                      ))}
                    </Grid>
                  </Show>
                </VStack>
              </Card>

              <RequestTabMaterialItemPrice
                control={control}
                isEditingDisabled={isEditingDisabled}
                directSaleId={props.directSaleId}
                itemId={props.item.id}
                quantitySchema={quantitySchema}
                onPriceChange={(currentPrices) => handlePriceChange(currentPrices, formApi)}
                data-testid={suffixTestId('prices', props)}
              />

              <Grid columns={3}>
                <Segment<ListSegment>
                  value={listSegment}
                  onChange={setListSegment}
                  options={segments}
                />
              </Grid>
              {segment}

              <DialogFooter>
                <ButtonGroup align="right">
                  <Button
                    variant="secondary"
                    title={i18n.t('general.actions.discard')}
                    onClick={props.onClose}
                    data-testid={suffixTestId('actions.discard', props)}
                  />
                  <Button
                    leftIcon="action/open_in_new"
                    variant="secondary"
                    title={i18n.t('entity.warehouse.labels.articleDetail')}
                    onClick={handleArticleDetail}
                    data-testid={suffixTestId('actions.articleDetail', props)}
                  />
                  <FormButton
                    isDisabled={!isTouched || hasQuantityError}
                    type="submit"
                    control={control}
                    title={i18n.t('general.labels.saveChanges')}
                    isLoading={patchMaterialItemStatus.isLoading}
                    data-testid={suffixTestId('actions.submit', props)}
                  />
                </ButtonGroup>
              </DialogFooter>
            </VStack>
          );
        }}
      </Form>
    </DataStatus>
  );
}

const discountRateSchema = z.number().min(0).max(100).nullish();
